/**
 * Canonical cover period labels and TPO/TOR product rules.
 * Prevents stale "1 Month" (from TOR) being kept on annual TPO checkout.
 */

export function normalizeCoverPeriodLabel(period) {
  const p = String(period || '').trim();
  if (!p) return '';
  const lower = p.toLowerCase();
  if (lower === '12 months' || lower === '12 month' || lower === '1year' || lower === '1 year') return '1 Year';
  if (lower === '1month' || lower === '1 month') return '1 Month';
  if (lower.includes('6') && lower.includes('month')) return '6 Months';
  if (p === '1 Year (50%)') return '1 Year (50%)';
  return p;
}

/**
 * @param {string} coverType TPO | TOR | TPO_EXTEND
 * @param {string} coverPeriod raw period from UI state
 * @param {string} vehicleClass
 * @param {object} [app] optional application payload (extension flags)
 */
export function canonicalCoverPeriodForProduct(coverType, coverPeriod, vehicleClass, app = {}) {
  const ct = String(coverType || 'TPO').toUpperCase();
  const period = normalizeCoverPeriodLabel(coverPeriod);
  const cls = String(vehicleClass || '').trim();

  if (ct === 'TOR') return period || '1 Month';
  if (ct === 'COMP' || ct === 'COMPREHENSIVE') return '1 Month';

  const hasParentPolicy = !!(
    String(app?.extensionPolicyNo || app?.extension_policy_no || '').trim()
  );
  const isExtend =
    ct === 'TPO_EXTEND' ||
    app?.productVariant === 'TPO_EXTEND' ||
    app?.product_variant === 'TPO_EXTEND' ||
    (app?.extensionMode && hasParentPolicy);

  if (isExtend) {
    // Second-half: remaining annual period from first purchase start + 1 year
    if (hasParentPolicy) return '1 Year (50%)';
    if (period === '1 Year (50%)') return '1 Year (50%)';
    if (period === '6 Months' || cls === 'Motorcycle PSV 6 Months') return '6 Months';
    // Fresh Buy/Extend: always 1 Month @ 50%
    return '1 Month';
  }

  if (ct === 'TPO') {
    if (period === '6 Months' || cls === 'Motorcycle PSV 6 Months') return '6 Months';
    if (period === '1 Year (50%)') return '1 Year (50%)';
    // Plain TPO is annual — never keep a stale TOR "1 Month" unless productVariant says extend.
    return '1 Year';
  }

  return period || '1 Year';
}

/**
 * Period key used to match rows in `tpoPricing` / product_rates (e.g. Motorcycle PSV 6 Months → "6 Months").
 */
export function tpoPremiumLookupPeriod(coverType, coverPeriod, vehicleClass, app = {}) {
  return canonicalCoverPeriodForProduct(coverType, coverPeriod, vehicleClass, app);
}

/** True when TPO checkout has an explicit user-selected period (blocks early reserve with default 1 Year). */
export function isTpoCoverPeriodChosen(coverType, coverPeriod) {
  const ct = String(coverType || '').toUpperCase();
  if (ct === 'TOR') return true;
  if (ct === 'COMP' || ct === 'COMPREHENSIVE') {
    return normalizeCoverPeriodLabel(coverPeriod) === '1 Month';
  }
  if (ct === 'TPO_EXTEND') return true;
  if (ct === 'TPO') return String(coverPeriod || '').trim() !== '';
  return true;
}
